import type { DeliveryAddressForm } from '../components/DeliveryAddressForm';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export type SendcloudRawAddress = {
  address_line_1: string;
  house_number?: string;
  address_line_2?: string;
  postal_code: string;
  city: string;
  country_code: string;
  state_province_code?: string;
};

type SendcloudValidateResponse = {
  input_address_is_valid?: boolean | null;
  results?: Array<{
    address?: SendcloudRawAddress | null;
    recommended?: boolean;
    validation_method?: string | null;
    analysis?: {
      validation_result?: { is_valid?: boolean };
      invalid_attributes?: string[];
    } | null;
  }>;
};

function nonempty(value?: string | null): string {
  return (value ?? '').trim();
}

function isIsoProvinceCode(value: string): boolean {
  return /^[A-Za-z]{2}(-[A-Za-z0-9]{1,3})?$/.test(value);
}

function toCountryCode(country?: string | null): string {
  const value = (country || '').trim();
  if (value.length === 2) return value.toUpperCase();
  const normalized = value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (normalized === 'espana' || normalized === 'spain') return 'ES';
  if (normalized === 'portugal') return 'PT';
  if (normalized === 'francia' || normalized === 'france') return 'FR';
  return 'ES';
}

function keepCountry(current?: string | null, incoming?: string | null): string {
  const next = nonempty(incoming);
  if (!next) return nonempty(current) || 'ES';
  if (toCountryCode(current) === toCountryCode(next)) return nonempty(current) || next;
  return next;
}

export function mapClientAddressToValidatePayload(address: DeliveryAddressForm) {
  const line2 = nonempty(address.recepient_line_2);
  const province = nonempty(address.recepient_province);
  return {
    address: {
      address_line_1: address.recepient_line_1.trim(),
      postal_code: address.recepient_postal_code.trim(),
      city: address.recepient_city.trim(),
      country_code: toCountryCode(address.recepient_country),
      ...(line2 ? { address_line_2: line2 } : {}),
      ...(isIsoProvinceCode(province) ? { state_province_code: province.toUpperCase() } : {}),
    },
    carrier_code: 'mrw',
    validation_methods: ['here'],
  };
}

export function isSendcloudAddressValid(response: SendcloudValidateResponse): boolean {
  const here = response.results?.filter((result) => result.validation_method === 'here') ?? [];
  if (here.length > 0) {
    return here.some(
      (result) =>
        result.recommended === true && result.analysis?.validation_result?.is_valid === true,
    );
  }
  return response.input_address_is_valid === true;
}

export function pickRecommendedSendcloudAddress(
  response: SendcloudValidateResponse,
): SendcloudRawAddress | null {
  return response.results?.find((result) => result.recommended && result.address)?.address ?? null;
}

const INVALID_ATTR_LABELS: Record<string, string> = {
  postal_code: 'código postal',
  city: 'ciudad',
  address_line_1: 'dirección',
  address_line_2: 'dirección (línea 2)',
  house_number: 'número',
  country_code: 'país',
  state_province_code: 'provincia',
};

const INVALID_ADDRESS_FALLBACK = 'La dirección no es real, comprueba los campos introducidos';

const ATTR_TO_CLIENT: Record<string, keyof DeliveryAddressForm> = {
  postal_code: 'recepient_postal_code',
  city: 'recepient_city',
  address_line_1: 'recepient_line_1',
  house_number: 'recepient_line_1',
  address_line_2: 'recepient_line_2',
  country_code: 'recepient_country',
  state_province_code: 'recepient_province',
};

export function sendcloudInvalidAttributes(response: SendcloudValidateResponse): string[] {
  return [
    ...new Set(
      (response.results ?? []).flatMap((result) => result.analysis?.invalid_attributes ?? []),
    ),
  ].filter(Boolean);
}

export function sendcloudInvalidClientFields(
  response: SendcloudValidateResponse,
): Array<keyof DeliveryAddressForm> {
  return [
    ...new Set(
      sendcloudInvalidAttributes(response)
        .map((attr) => ATTR_TO_CLIENT[attr])
        .filter((field): field is keyof DeliveryAddressForm => Boolean(field)),
    ),
  ];
}

export function formatSendcloudInvalidMessage(response: SendcloudValidateResponse): string {
  const labels = sendcloudInvalidAttributes(response)
    .map((attr) => INVALID_ATTR_LABELS[attr])
    .filter(Boolean);
  if (labels.length === 0) return INVALID_ADDRESS_FALLBACK;
  return `Revisa: ${labels.join(', ')}`;
}

export function formatSendcloudStreet(address: SendcloudRawAddress): string {
  const line1 = [address.address_line_1, address.house_number].filter(Boolean).join(' ');
  return [
    line1,
    address.address_line_2,
    `${address.postal_code ?? ''} ${address.city ?? ''}`.trim(),
    address.country_code,
  ].filter(Boolean).join(', ');
}

export function applyRecommendedToClientAddress(
  form: DeliveryAddressForm,
  recommended: SendcloudRawAddress,
): DeliveryAddressForm {
  const line1 = nonempty(recommended.address_line_1);
  const house = nonempty(recommended.house_number);
  const line2 = nonempty(recommended.address_line_2);
  const province = nonempty(recommended.state_province_code);
  return {
    ...form,
    recepient_line_1: house && line1 && !line1.includes(house) ? `${line1} ${house}` : line1 || house || form.recepient_line_1,
    recepient_line_2: line2 || form.recepient_line_2,
    recepient_postal_code: nonempty(recommended.postal_code) || form.recepient_postal_code,
    recepient_city: nonempty(recommended.city) || form.recepient_city,
    recepient_province: province || form.recepient_province,
    recepient_country: keepCountry(form.recepient_country, recommended.country_code),
  };
}

export function recommendedChangesClientForm(
  form: DeliveryAddressForm,
  recommended: SendcloudRawAddress,
): boolean {
  const next = applyRecommendedToClientAddress(form, recommended);
  return (
    nonempty(next.recepient_line_1) !== nonempty(form.recepient_line_1) ||
    nonempty(next.recepient_line_2) !== nonempty(form.recepient_line_2) ||
    nonempty(next.recepient_postal_code) !== nonempty(form.recepient_postal_code) ||
    nonempty(next.recepient_city) !== nonempty(form.recepient_city) ||
    nonempty(next.recepient_province) !== nonempty(form.recepient_province) ||
    nonempty(next.recepient_country) !== nonempty(form.recepient_country)
  );
}

export async function validateClientAddress(
  address: DeliveryAddressForm,
): Promise<SendcloudValidateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/sendcloud/addresses/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(mapClientAddressToValidatePayload(address)),
  });

  const text = await response.text();
  if (!response.ok) {
    throw new Error('No se pudo comprobar la dirección');
  }
  if (!text) return {};
  return JSON.parse(text) as SendcloudValidateResponse;
}
